/**
 * PDF Parsing Service for P&ID Tag Extraction
 * Extracts equipment and instrument tags from P&ID documents
 * Following ISA 5.1 standard for instrument identification
 * Supports OCR for scanned/image-based PDFs using Tesseract.js
 */

import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import Tesseract from 'tesseract.js';

// Configure PDF.js worker
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
}

class PDFParsingService {
  constructor() {
    // ISA 5.1 Standard Tag Patterns (expanded for OCR compatibility)
    this.tagPatterns = {
      // Equipment tags: Letter(s)-Number(s) with optional suffixes
      equipment: [
        /\b[A-Z]{1,3}-\d{4}[A-Z]?\b/gi,        // K-2801, V-3701A
        /\b[A-Z]{1,3}\d{4}[A-Z]?\b/gi,         // K2801, V3701A, T8557
        /\b[A-Z]{1,3}-\d{3}[A-Z]?\b/gi,        // P-101, C-201B
        /\b[A-Z]{1,3}\s*-\s*\d{4}[A-Z]?\b/gi,  // K - 2801
        /\b[A-Z]{1,2}\d{3,5}[A-Z]?\b/gi,       // T8557, V1001, P101 (flexible)
        /\b[A-Z]{1,3}-\d{2,5}[A-Z]?\b/gi,      // E-101, H-2001, D-50
        /\d{2}-[A-Z]{1,3}-\d{3,5}/gi,          // 14-PT-1001 format
      ],

      // Instrument tags with loop numbers
      instruments: [
        /\b[A-Z]{2,4}-\d{4,6}\b/gi,            // PIC-10001, LIC-2002
        /\b[A-Z]{2,4}\d{4,6}\b/gi,             // PIC10001, LIC2002
        /\b[A-Z]{1}[A-Z]{1,3}-\d{3,5}\b/gi,    // PT-101, FT-2001
        /\b[A-Z]{2,4}\s*-\s*\d{4,6}\b/gi,      // PIC - 10001
        /\b[PFLTAIC]{1}[ICTRSAHLE]{1,3}-?\d{3,6}\b/gi, // Common instrument patterns
        /\d{2}-[PFLTAIC][ICTRSAHLE]{1,2}-\d{3,5}/gi,   // 14-PT-1001, 71-FIC-001
      ],

      // Control valves and special equipment
      controlValves: [
        /\b[A-Z]{2,3}V-\d{3,4}[A-Z]?\b/gi,     // PCV-101, FCV-2001
        /\b[A-Z]{2,3}V\d{3,4}[A-Z]?\b/gi,      // PCV101, FCV2001
        /\bHV-?\d{3,4}[A-Z]?\b/gi,             // HV-101, HV101 (Hand Valve)
        /\bXV-?\d{3,4}[A-Z]?\b/gi,             // XV-101, XV101 (Safety Valve)
        /\bSDV-?\d{3,4}[A-Z]?\b/gi,            // SDV-101 (Shutdown Valve)
        /\bBDV-?\d{3,4}[A-Z]?\b/gi,            // BDV-101 (Blowdown Valve)
        /\bPSV-?\d{3,4}[A-Z]?\b/gi,            // PSV-101 (Pressure Safety Valve)
      ],

      // Line numbers (process lines)
      lineNumbers: [
        /\b\d{1,3}"-[A-Z]{1,3}-\d{4,6}\b/gi,   // 6"-PG-10001 (size-service-number)
        /\b\d{1,3}['']?-[A-Z]{1,3}-\d{4,6}\b/gi, // 6'-PG-10001
        /\b\d{1,2}"-[A-Z]{2,4}-\d{3,6}[A-Z]?\b/gi, // 2"-HC-1001A
        /\bLINE-?\d{3,6}\b/gi,                 // LINE-1001
      ]
    };

    // ISA 5.1 First Letter Designations
    this.isaFirstLetters = {
      A: 'Analysis',
      B: 'Burner/Combustion',
      C: 'Conductivity',
      D: 'Density/Specific Gravity',
      E: 'Voltage',
      F: 'Flow',
      G: 'Gauging/Position',
      H: 'Hand',
      I: 'Current',
      J: 'Power',
      K: 'Time/Schedule',
      L: 'Level',
      M: 'Moisture/Humidity',
      N: 'User Choice',
      O: 'User Choice',
      P: 'Pressure/Vacuum',
      Q: 'Quantity',
      R: 'Radiation',
      S: 'Speed/Frequency',
      T: 'Temperature',
      U: 'Multivariable',
      V: 'Vibration',
      W: 'Weight/Force',
      X: 'Unclassified',
      Y: 'Event/State',
      Z: 'Position/Dimension'
    };

    // ISA 5.1 Succeeding Letter Functions
    this.isaSucceedingLetters = {
      A: 'Alarm',
      B: 'User Choice',
      C: 'Control',
      D: 'Differential',
      E: 'Element',
      F: 'Ratio',
      G: 'Glass/Gauge',
      H: 'High',
      I: 'Indicate',
      J: 'Scan',
      K: 'Control Station',
      L: 'Light/Low',
      M: 'Middle/Intermediate',
      N: 'User Choice',
      O: 'Orifice',
      P: 'Point/Test',
      Q: 'Integrate/Totalize',
      R: 'Record',
      S: 'Switch',
      T: 'Transmit',
      U: 'Multifunction',
      V: 'Valve',
      W: 'Well',
      X: 'Unclassified',
      Y: 'Relay/Compute',
      Z: 'Driver/Actuator'
    };

    // Common equipment type codes (expanded for better OCR matching)
    this.equipmentCodes = {
      // Vessels & Tanks
      V: 'Vessel',
      T: 'Tower/Column',
      D: 'Drum',
      TK: 'Tank',
      VE: 'Vessel',
      DR: 'Drum',

      // Rotating Equipment
      P: 'Pump',
      K: 'Compressor',
      B: 'Blower',
      F: 'Fan',
      A: 'Agitator',
      M: 'Motor',
      G: 'Generator',
      J: 'Ejector',

      // Heat Transfer
      E: 'Exchanger',
      H: 'Heater',
      C: 'Cooler/Condenser',
      R: 'Reactor',
      HE: 'Heat Exchanger',
      AC: 'Air Cooler',

      // Separation & Filtration
      S: 'Separator',
      FLT: 'Filter',
      FL: 'Filter',
      MX: 'Mixer',
      Y: 'Strainer',
      CY: 'Cyclone',

      // Piping & Valves
      L: 'Line',
      X: 'Miscellaneous',
      N: 'Equipment',
      W: 'Equipment',
      Z: 'Equipment',

      // Generic catch-all for OCR
      Q: 'Equipment',
      U: 'Equipment',
      O: 'Equipment',
      I: 'Equipment'
    };
  }

  /**
   * Extract tags from a PDF file
   * @param {File} file - PDF file to parse
   * @returns {Promise<Object>} Extracted tags and metadata
   */
  async extractTags(file) {
    try {
      const startTime = performance.now();

      // Convert file to ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();

      // Load PDF document
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const numPages = pdf.numPages;

      const extractedData = {
        metadata: {
          fileName: file.name,
          fileSize: file.size,
          pageCount: numPages,
          extractionDate: new Date().toISOString(),
          processingTime: 0
        },
        tags: {
          equipment: new Map(),
          instruments: new Map(),
          controlValves: new Map(),
          lineNumbers: new Map()
        },
        summary: {
          totalTags: 0,
          equipmentCount: 0,
          instrumentCount: 0,
          controlValveCount: 0,
          lineNumberCount: 0
        },
        pageDetails: []
      };

      // Extract text from each page
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        let pageText = textContent.items.map(item => item.str).join(' ');

        // Debug: Log extracted text
        console.log(`[PDF DEBUG] Page ${pageNum} - Text items count:`, textContent.items.length);
        console.log(`[PDF DEBUG] Page ${pageNum} - Raw text (first 1000 chars):`, pageText.substring(0, 1000));
        console.log(`[PDF DEBUG] Page ${pageNum} - Full text length:`, pageText.length);

        // OCR Fallback: If no text layer found, use Tesseract OCR
        if (pageText.trim().length === 0) {
          console.log(`[PDF DEBUG] Page ${pageNum} - No text layer found, using OCR...`);
          try {
            // Render page to canvas with HIGH resolution for better OCR
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            const viewport = page.getViewport({ scale: 3.0 }); // Higher scale = better OCR accuracy
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            // Fill white background (helps OCR)
            context.fillStyle = 'white';
            context.fillRect(0, 0, canvas.width, canvas.height);

            await page.render({ canvasContext: context, viewport }).promise;

            // Run OCR with optimized settings for engineering drawings
            console.log(`[PDF DEBUG] Page ${pageNum} - Running Tesseract OCR (this may take a moment)...`);
            const { data: { text: ocrText } } = await Tesseract.recognize(canvas, 'eng', {
              logger: m => {
                if (m.status === 'recognizing text') {
                  console.log(`[PDF DEBUG] OCR Progress: ${Math.round(m.progress * 100)}%`);
                }
              }
            });
            pageText = ocrText;
            console.log(`[PDF DEBUG] Page ${pageNum} - OCR extracted text (first 1000 chars):`, pageText.substring(0, 1000));
            console.log(`[PDF DEBUG] Page ${pageNum} - OCR text length:`, pageText.length);
          } catch (ocrError) {
            console.error(`[PDF DEBUG] Page ${pageNum} - OCR failed:`, ocrError);
          }
        }

        const pageTags = this.extractTagsFromText(pageText);

        // Aggregate tags
        pageTags.equipment.forEach(tag => {
          const existing = extractedData.tags.equipment.get(tag.tag) || { ...tag, occurrences: [] };
          existing.occurrences.push({ page: pageNum, context: tag.context });
          extractedData.tags.equipment.set(tag.tag, existing);
        });

        pageTags.instruments.forEach(tag => {
          const existing = extractedData.tags.instruments.get(tag.tag) || { ...tag, occurrences: [] };
          existing.occurrences.push({ page: pageNum, context: tag.context });
          extractedData.tags.instruments.set(tag.tag, existing);
        });

        pageTags.controlValves.forEach(tag => {
          const existing = extractedData.tags.controlValves.get(tag.tag) || { ...tag, occurrences: [] };
          existing.occurrences.push({ page: pageNum, context: tag.context });
          extractedData.tags.controlValves.set(tag.tag, existing);
        });

        pageTags.lineNumbers.forEach(tag => {
          const existing = extractedData.tags.lineNumbers.get(tag.tag) || { ...tag, occurrences: [] };
          existing.occurrences.push({ page: pageNum, context: tag.context });
          extractedData.tags.lineNumbers.set(tag.tag, existing);
        });

        extractedData.pageDetails.push({
          pageNumber: pageNum,
          tagsFound: pageTags.equipment.length + pageTags.instruments.length +
                    pageTags.controlValves.length + pageTags.lineNumbers.length
        });
      }

      // Convert Maps to Arrays and update summary
      extractedData.tags.equipment = Array.from(extractedData.tags.equipment.values());
      extractedData.tags.instruments = Array.from(extractedData.tags.instruments.values());
      extractedData.tags.controlValves = Array.from(extractedData.tags.controlValves.values());
      extractedData.tags.lineNumbers = Array.from(extractedData.tags.lineNumbers.values());

      // Update summary counts
      extractedData.summary.equipmentCount = extractedData.tags.equipment.length;
      extractedData.summary.instrumentCount = extractedData.tags.instruments.length;
      extractedData.summary.controlValveCount = extractedData.tags.controlValves.length;
      extractedData.summary.lineNumberCount = extractedData.tags.lineNumbers.length;
      extractedData.summary.totalTags = extractedData.summary.equipmentCount +
                                      extractedData.summary.instrumentCount +
                                      extractedData.summary.controlValveCount +
                                      extractedData.summary.lineNumberCount;

      // Calculate processing time
      extractedData.metadata.processingTime = Math.round(performance.now() - startTime);

      return extractedData;

    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw new Error(`Failed to parse PDF: ${error.message}`);
    }
  }

  /**
   * Extract tags from text content
   * @param {string} text - Text to parse
   * @returns {Object} Extracted tags by category
   */
  extractTagsFromText(text) {
    const results = {
      equipment: [],
      instruments: [],
      controlValves: [],
      lineNumbers: []
    };

    // Normalize text (remove excessive whitespace)
    text = text.replace(/\s+/g, ' ');

    // Extract equipment tags
    this.tagPatterns.equipment.forEach(pattern => {
      const matches = text.match(pattern) || [];
      matches.forEach(match => {
        const normalized = this.normalizeTag(match);
        if (this.isValidEquipmentTag(normalized) && !this.isDuplicate(results.equipment, normalized)) {
          results.equipment.push({
            tag: normalized,
            type: this.identifyEquipmentType(normalized),
            description: this.generateDescription(normalized, 'equipment'),
            context: this.extractContext(text, match)
          });
        }
      });
    });

    // Extract instrument tags
    this.tagPatterns.instruments.forEach(pattern => {
      const matches = text.match(pattern) || [];
      matches.forEach(match => {
        const normalized = this.normalizeTag(match);
        if (this.isValidInstrumentTag(normalized) && !this.isDuplicate(results.instruments, normalized)) {
          results.instruments.push({
            tag: normalized,
            type: this.identifyInstrumentType(normalized),
            description: this.generateDescription(normalized, 'instrument'),
            context: this.extractContext(text, match)
          });
        }
      });
    });

    // Extract control valve tags
    this.tagPatterns.controlValves.forEach(pattern => {
      const matches = text.match(pattern) || [];
      matches.forEach(match => {
        const normalized = this.normalizeTag(match);
        if (!this.isDuplicate(results.controlValves, normalized)) {
          results.controlValves.push({
            tag: normalized,
            type: 'Control Valve',
            description: this.generateDescription(normalized, 'controlValve'),
            context: this.extractContext(text, match)
          });
        }
      });
    });

    // Extract line numbers
    this.tagPatterns.lineNumbers.forEach(pattern => {
      const matches = text.match(pattern) || [];
      matches.forEach(match => {
        const normalized = this.normalizeTag(match);
        if (!this.isDuplicate(results.lineNumbers, normalized)) {
          results.lineNumbers.push({
            tag: normalized,
            type: 'Process Line',
            description: this.generateDescription(normalized, 'line'),
            context: this.extractContext(text, match)
          });
        }
      });
    });

    return results;
  }

  /**
   * Normalize a tag (remove spaces, uppercase)
   */
  normalizeTag(tag) {
    return tag.replace(/\s+/g, '').toUpperCase();
  }

  /**
   * Check if tag is valid equipment tag (lenient for OCR)
   */
  isValidEquipmentTag(tag) {
    // More lenient validation for OCR output
    // Just check basic structure: 1-3 letters followed by numbers
    const match = tag.match(/^([A-Z]{1,3})/i);
    if (!match) return false;

    const prefix = match[1].toUpperCase();
    // Accept if prefix is in equipmentCodes OR if it follows basic pattern
    return this.equipmentCodes.hasOwnProperty(prefix) || /^[A-Z]{1,3}[-]?\d{2,5}/i.test(tag);
  }

  /**
   * Check if tag is valid instrument tag (lenient for OCR)
   */
  isValidInstrumentTag(tag) {
    // More lenient validation for OCR output
    const match = tag.match(/^([A-Z]{1,4})/i);
    if (!match) return false;

    const letters = match[1].toUpperCase();
    // Accept if first letter is valid ISA designation OR if it matches instrument pattern
    return this.isaFirstLetters.hasOwnProperty(letters[0]) || /^[A-Z]{2,4}[-]?\d{3,6}/i.test(tag);
  }

  /**
   * Identify equipment type from tag
   */
  identifyEquipmentType(tag) {
    const match = tag.match(/^([A-Z]{1,3})/);
    if (match && this.equipmentCodes[match[1]]) {
      return this.equipmentCodes[match[1]];
    }
    return 'Equipment';
  }

  /**
   * Identify instrument type from tag
   */
  identifyInstrumentType(tag) {
    const match = tag.match(/^([A-Z]{2,4})/);
    if (match) {
      const letters = match[1];
      const variable = this.isaFirstLetters[letters[0]] || 'Unknown';
      const functions = letters.slice(1).split('').map(l =>
        this.isaSucceedingLetters[l] || ''
      ).filter(f => f).join('/');

      return `${variable} ${functions}`.trim();
    }
    return 'Instrument';
  }

  /**
   * Generate description for a tag
   */
  generateDescription(tag, category) {
    switch (category) {
      case 'equipment':
        return `${this.identifyEquipmentType(tag)} - ${tag}`;
      case 'instrument':
        return `${this.identifyInstrumentType(tag)} - ${tag}`;
      case 'controlValve':
        return `Control Valve - ${tag}`;
      case 'line':
        return `Process Line - ${tag}`;
      default:
        return tag;
    }
  }

  /**
   * Extract context around a match
   */
  extractContext(text, match) {
    const index = text.indexOf(match);
    const start = Math.max(0, index - 30);
    const end = Math.min(text.length, index + match.length + 30);
    return '...' + text.slice(start, end) + '...';
  }

  /**
   * Check if tag already exists in results
   */
  isDuplicate(array, tag) {
    return array.some(item => item.tag === tag);
  }

  /**
   * Generate tag statistics
   */
  generateStatistics(extractedData) {
    const stats = {
      byType: {},
      byPage: {},
      duplicates: 0,
      unique: extractedData.summary.totalTags
    };

    // Count by equipment type
    extractedData.tags.equipment.forEach(tag => {
      stats.byType[tag.type] = (stats.byType[tag.type] || 0) + 1;
    });

    // Count by page
    extractedData.pageDetails.forEach(page => {
      stats.byPage[`Page ${page.pageNumber}`] = page.tagsFound;
    });

    return stats;
  }
}

// Create singleton instance
const pdfParsingService = new PDFParsingService();

export default pdfParsingService;